import { Component, DestroyRef, inject, OnInit, signal, ViewChild, WritableSignal } from '@angular/core';
import { CreativeGalleryComponent } from '../../my-brand-screen/creative-gallery/creative-gallery.component';
import { NgIf } from '@angular/common';
import { PaginatorComponent } from '../../../shared/components/paginator/paginator.component';
import { CreativeGalleryResponse, GalleryNavigation } from '../../my-brand-screen/types';
import { PageEvent } from '@angular/material/paginator';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DashboardService, SelectBrandItem } from '../../../services/dashboard.service';
import { MessagingService } from '../../../services/messaging.service';
import { MessagingCreativeGalleryPayload } from '../types';
import { catchError, EMPTY, Subject, switchMap, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AssetVersionPipe } from "../../../pipes/asset-version.pipe";

export interface CreativeGalleryDrawerData {
  brandKey: string;
}

@Component({
  selector: 'app-creative-gallery-drawer',
  imports: [
    CreativeGalleryComponent,
    NgIf,
    PaginatorComponent,
    AssetVersionPipe
  ],
  templateUrl: './creative-gallery-drawer.component.html',
  styleUrl: './creative-gallery-drawer.component.scss'
})
export class CreativeGalleryDrawerComponent implements OnInit {

  @ViewChild('creativeGalleryComponent') creativeGalleryCmp!: CreativeGalleryComponent;
  @ViewChild('creativeGalleryPaginator') creativeGalleryPaginator!: PaginatorComponent;

  private readonly messagingService = inject(MessagingService);
  private readonly dashboardService = inject(DashboardService);
  private readonly data = inject(MAT_DIALOG_DATA);
  private readonly destroyRef = inject(DestroyRef);
  private pendingGalleryModalNav?: GalleryNavigation;
  private loadCreativeGallery$ = new Subject<MessagingCreativeGalleryPayload>()
  public creativeGalleryData$: WritableSignal<CreativeGalleryResponse> = signal<CreativeGalleryResponse>({
    creativesData: [],
    total: 0
  });
  public isLoading = false;
  public brand?: SelectBrandItem;
  public brandKey!: string;
  public theme!: string;
  public close!: () => void;

  public initialPage: number = 1;
  public CREATIVE_GALLERY_PAGE_SIZE: number = 8;
  public creativeGalleryCurrentPage: number = 0;


  public ngOnInit(): void {
    this.isLoading = true;
    this.brandKey = this.data?.data.brandKey;
    this.brand = [this.dashboardService.myBrand(), ...this.dashboardService.myCompetitors()]
      .find((brand) => brand.key === this.brandKey);
    this.theme = this.data?.data.theme;
    this.close = this.data?.data.close;
    this.initCreativeGallerySubject();
    this.loadCreativeGalleryWithPayload(this.getCreativeGalleryPayload());
  }

  public onCreativeModalEdgeNav(direction: GalleryNavigation) {
    const total = this.creativeGalleryData$().total;
    const pageSize = this.CREATIVE_GALLERY_PAGE_SIZE;
    const lastPageIndex = Math.max(0, Math.ceil(total / pageSize) - 1);
    const targetIndex = direction === 'next' ? this.creativeGalleryCurrentPage + 1 : this.creativeGalleryCurrentPage - 1;
    this.pendingGalleryModalNav = direction;

    if (targetIndex < 0) {
      this.creativeGalleryPaginator?.goToPage(lastPageIndex + 1);
      return;
    }
    if (targetIndex > lastPageIndex) {
      this.creativeGalleryPaginator?.goToPage(1);
      return;
    }
    this.creativeGalleryPaginator?.goToPage(targetIndex + 1);
  }

  public onCreativeGalleryPageChange(event: PageEvent) {
    const pageIndex = this.creativeGalleryCurrentPage = event.pageIndex as number;
    this.loadCreativeGalleryWithPayload({
      brandsKeys: [this.brandKey],
      themes: [this.theme],
      limit: this.CREATIVE_GALLERY_PAGE_SIZE,
      offset: pageIndex * this.CREATIVE_GALLERY_PAGE_SIZE,
      ...this.dashboardService.getDashboardFiltersPayload()
    })
  }

  public onIconError(event: Event): void {
    (event.target as HTMLImageElement).src = 'images/no-logo.png';
  }

  private getCreativeGalleryPayload(): MessagingCreativeGalleryPayload {
    return {
      brandsKeys: [this.brandKey],
      limit: this.CREATIVE_GALLERY_PAGE_SIZE,
      offset: this.creativeGalleryCurrentPage,
      themes: [this.theme],
      ...this.dashboardService.getDashboardFiltersPayload()
    }
  }

  private initCreativeGallerySubject(): void {
    this.isLoading = true;
    this.loadCreativeGallery$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.isLoading = true),
        switchMap((payload) =>
          this.messagingService.getCreativeGallery(payload)
            .pipe(
              catchError(() => {
                this.creativeGalleryData$.set({ creativesData: [], total: 0 });
                return EMPTY;
              })
            )
        ),
      )
      .subscribe(result => {
        if (result) {
          this.creativeGalleryData$.set(result);
          if (this.pendingGalleryModalNav && this.creativeGalleryData$().creativesData.length) {
            const openFirst = this.pendingGalleryModalNav === GalleryNavigation.NEXT;
            const target = openFirst ? this.creativeGalleryData$().creativesData[0] : this.creativeGalleryData$().creativesData[this.creativeGalleryData$().creativesData.length - 1];
            this.pendingGalleryModalNav = undefined;
            setTimeout(() => this.creativeGalleryCmp.openCardModal(target.id, 'navigate'), 0);
          }
        }
        this.isLoading = false;
      })
  }

  private loadCreativeGalleryWithPayload(payload: MessagingCreativeGalleryPayload): void {
    this.loadCreativeGallery$.next(payload);
  }
}
